/**
 * SFrame137 – IAD Step 3: Secure and Maintain Vehicles
 * Refactored to use the standardized ChecklistItem template with Notes support.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ProgressHeader from '../settlement/ProgressHeader';
import ChecklistItem from '../settlement/ChecklistItem'; 
import { useSettlementSession } from '../../hooks/useSettlementSession';
import {
  loadResidencePanelCompletion,
  withEstatePath,
} from '../../utils/secureResidenceFlow';
import { SETTLEMENT_PLANNER_LAYOUT } from '../settlement/settlementTheme';

interface FrameDataWithNotes {
  completed?: boolean;
  notes?: string;
}

const PHASE = 'iad';
const STEP_ID = 'iad-3';
const OVERVIEW_ROUTE = '/sframe-141';
const isPlanner = true;

const containerClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.container : '';
const contentColumnClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.contentColumn : '';
const titleClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.title : 'font-display text-[28px] leading-tight text-blue-900';
const subtitleClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.subtitle : 'text-sm text-grey-600 mb-6 leading-relaxed';

const VEHICLE_PANELS = [
  { frameId: 'frame-vehicle-secure', title: 'Secure Vehicles', intro: 'Make sure all vehicles are safe and accounted for.' },
  { frameId: 'frame-vehicle-keys', title: 'Locate Keys and Documents', intro: 'Find keys, titles, and important vehicle info.' },
  { frameId: 'frame-vehicle-insurance', title: 'Confirm Insurance Coverage', intro: 'Make sure vehicles remain insured.' },
  { frameId: 'frame-vehicle-condition', title: 'Check Vehicle Condition', intro: 'Do a quick condition check.' },
  { frameId: 'frame-vehicle-plan', title: 'Decide Short-Term Plan', intro: 'Determine what to do with vehicles for now.' },
];

const CHECKLIST_GUIDANCE = [
  {
    what: 'Make sure all vehicles are safe and accounted for.',
    why: 'Prevents theft, misuse, or loss.',
    actions: ['Locate all vehicles (garage, street, storage)', 'Move to a secure location if needed', 'Confirm they are locked', 'Limit who has access'],
  },
  {
    what: 'Find keys, titles, and important vehicle info.',
    why: 'You’ll need these later for transfer or sale.',
    actions: ['Collect all keys and spare keys', 'Locate registration and title documents', 'Check glove box and home records', 'Store in a secure place'],
  },
  {
    what: 'Make sure vehicles remain insured.',
    why: 'Uninsured vehicles create liability risk.',
    actions: ['Identify current insurance provider', 'Confirm policies are still active', 'Notify insurer if needed', 'Document contact info'],
  },
  {
    what: 'Do a quick condition check.',
    why: 'Prevents damage while the estate is being managed.',
    actions: ['Check battery, tires, and fuel level', 'Note any visible damage', 'Ensure vehicles are not at risk (weather, location)', 'Take photos if helpful'],
  },
  {
    what: 'Determine what to do with vehicles for now.',
    why: 'Avoids confusion or unauthorized use.',
    actions: ['Decide: keep parked, store, or allow limited use', 'Communicate plan to family if needed', 'Avoid selling or transferring yet', 'Revisit during asset distribution'],
  },
];

export default function SFrame137() {
  const { estateId: paramEstateId } = useParams<{ estateId: string }>();
  const estateId = paramEstateId || null;
  const navigate = useNavigate();
  
  const { error, loadFrameData, loadPhaseProgress, updateStepProgress, saveFrameData } = useSettlementSession(estateId);

  const [completedPanels, setCompletedPanels] = useState(0);
  const [panelDone, setPanelDone] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState('');

  const refresh = useCallback(async () => {
    if (!estateId) return;

    // Load checklist completion
    const { byFrameId } = await loadResidencePanelCompletion(estateId, loadFrameData);
    const vehicleFrameIds = VEHICLE_PANELS.map(p => p.frameId);
    const vehicleCount = Object.keys(byFrameId).filter(id => vehicleFrameIds.includes(id) && byFrameId[id]).length;
    
    setCompletedPanels(vehicleCount);
    setPanelDone(byFrameId);

    // Load notes
    const frameData = await loadFrameData(STEP_ID) as FrameDataWithNotes;
    if (frameData?.notes) setNotes(frameData.notes);
  }, [estateId, loadFrameData]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleToggleComplete = async (frameId: string, newState: boolean) => {
    const newPanelDone = { ...panelDone, [frameId]: newState };
    setPanelDone(newPanelDone);
    
    const vehicleFrameIds = VEHICLE_PANELS.map(p => p.frameId);
    setCompletedPanels(Object.keys(newPanelDone).filter(id => vehicleFrameIds.includes(id) && newPanelDone[id]).length);

    if (estateId) {
      await saveFrameData(frameId, { completed: newState });
      await refresh();
      
      const steps = await loadPhaseProgress(PHASE);
      const row = steps.find((s) => s.stepId === STEP_ID);
      if (row?.status === 'not-started' || !row) {
        await updateStepProgress(STEP_ID, PHASE, { status: 'in-progress' });
      }
    }
  };

  const handleNoteChange = async (val: string) => {
    setNotes(val);
    if (estateId) {
      await saveFrameData(STEP_ID, { notes: val } as Record<string, unknown>);
    }
  };

  return (
    <div className={containerClassName}>
      <div className={contentColumnClassName}>
        {error && (
          <div className="mb-4 bg-orange-100 border border-orange-300 rounded-md p-3 text-orange-900 text-sm max-w-3xl mx-auto">
            {error}
          </div>
        )}

        <div className="flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={() => navigate(withEstatePath(OVERVIEW_ROUTE, estateId))}
            className="w-[35px] h-[35px] flex items-center justify-center text-grey-600 hover:text-blue-900 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className={titleClassName}>Secure and Maintain Vehicles</h1>
        </div>

        <p className={subtitleClassName}>
          Locate, secure, and manage vehicles to prevent loss, damage, or unauthorized use.
        </p>

        <ProgressHeader
          title="Secure and maintain vehicles"
          variant={isPlanner ? 'planner' : 'default'}
          subtitle="Work through each item to ensure vehicles are safe and accounted for during this early stage."
          completedSteps={completedPanels}
          totalSteps={VEHICLE_PANELS.length}
          estimatedMinutes={45}
        />

        <div className="space-y-4 mt-8">
          {VEHICLE_PANELS.map((panel, index) => {
            const guidance = CHECKLIST_GUIDANCE[index];
            return (
              <ChecklistItem
                key={panel.frameId}
                id={panel.frameId}
                title={panel.title}
                description={panel.intro}
                whatThisIs={guidance.what}
                whyItMatters={guidance.why}
                bulletPoints={guidance.actions}
                checked={!!panelDone[panel.frameId]}
                onChange={handleToggleComplete}
              />
            );
          })}
        </div>

        {/* --- Notes Section --- */}
        <div className="mt-10 pt-10 border-t border-stone-200">
          <h3 className="text-lg font-semibold text-stone-900 mb-2">Notes</h3>
          <textarea
            className="w-full rounded-lg border border-stone-300 p-4 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all min-h-[120px]"
            placeholder="Add details, locations, or reminders to keep track of your progress here"            
            value={notes}
            onChange={(e) => handleNoteChange(e.target.value)}
          />
        </div>

      </div>
    </div>
  );
}